import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export default function Modal({ title, onClose, children, scrollOnDesktop = false }) {
  if (typeof document === 'undefined') return null
  const isCompact = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches
  const shouldScroll = true
  const cardRef = useRef(null)
  const [keyboardInset, setKeyboardInset] = useState(0)
  const [vvHeight, setVvHeight] = useState(0)

  const keepFocusedFieldVisible = (smooth = false) => {
    const card = cardRef.current
    const active = document.activeElement
    if (!card || !active || !card.contains(active)) return
    if (!(active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement)) return
    active.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' })
  }

  useEffect(() => {
    if (!isCompact || typeof window === 'undefined') return

    const updateKeyboardState = () => {
      const vv = window.visualViewport
      if (!vv) {
        setKeyboardInset(0)
        setVvHeight(window.innerHeight)
        return
      }

      setVvHeight(vv.height)
      const overlap = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop))
      // Ignore tiny viewport deltas from browser chrome; react only to keyboard-sized changes.
      setKeyboardInset(overlap > 80 ? overlap : 0)
    }

    updateKeyboardState()
    const vv = window.visualViewport
    vv?.addEventListener('resize', updateKeyboardState)
    vv?.addEventListener('scroll', updateKeyboardState)
    window.addEventListener('resize', updateKeyboardState)

    return () => {
      vv?.removeEventListener('resize', updateKeyboardState)
      vv?.removeEventListener('scroll', updateKeyboardState)
      window.removeEventListener('resize', updateKeyboardState)
    }
  }, [isCompact])

  useEffect(() => {
    if (!isCompact) return
    const onFocusIn = () => {
      requestAnimationFrame(() => keepFocusedFieldVisible(true))
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [isCompact])

  useEffect(() => {
    if (!isCompact || keyboardInset <= 0) return
    requestAnimationFrame(() => keepFocusedFieldVisible(true))
  }, [isCompact, keyboardInset])

  const mobileMaxHeight = useMemo(() => {
    if (!isCompact) return undefined
    if (!vvHeight) {
      return `calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 32px)`
    }
    return `${Math.max(280, vvHeight - 24)}px`
  }, [isCompact, vvHeight])

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: isCompact ? 'flex-end' : 'center',
    justifyContent: 'center',
    zIndex: 1000,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 0,
    paddingBottom: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
  }

  const cardStyle = {
    background: '#fff',
    borderRadius: isCompact ? '12px 12px 0 0' : 12,
    paddingTop: isCompact ? 12 : 16,
    paddingRight: 20,
    paddingLeft: 20,
    paddingBottom: isCompact ? 16 : 20,
    width: '100%',
    maxWidth: 520,
    minWidth: 0,
    marginBottom: isCompact ? keyboardInset : 0,
    maxHeight: shouldScroll
      ? (isCompact ? mobileMaxHeight : 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 32px)')
      : 'none',
    overflowY: shouldScroll ? 'auto' : 'visible',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    boxSizing: 'border-box',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    transition: isCompact ? 'margin-bottom 0.15s ease' : undefined,
  }

  return createPortal(
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose && onClose() }}>
      <div ref={cardRef} style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>{title}</h3>
          <button onClick={() => onClose && onClose()} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}
